import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { formatValue } from '@/lib/format';

// ============================================================================
// ReportResultTable — таблица результата отчёта (Ф1, ReportViewer):
// сортировка по клику заголовка (цикл ↑↓·; внутри групп — по сегментам),
// быстрый фильтр по значению, сворачивание групп (▸/▾ — данные скрываются,
// итоги остаются), форматирование значений по реквизитам определения (даты —
// через единый механизм форматов/часового пояса), ТРИ сообщения пустого
// результата. Пушдаун в SQL — Ф1.5 (здесь фильтрация/сортировка клиентские).
// ============================================================================

export interface ReportResultRow {
  type: string; // data|groupHeader|groupFooter|grandTotal
  level: number;
  data?: Record<string, unknown>;
  aggregates?: Record<string, unknown>;
}

export interface ReportResult {
  columns: string[];
  rows: ReportResultRow[];
}

interface FieldMeta { type: string; format: string }

interface Props {
  result: ReportResult;
  fieldTypes: Record<string, FieldMeta>;
  hasSources: boolean;
  paramsApplied: boolean;
}

const GROUP_TYPES = new Set(['groupHeader', 'groupFooter', 'grandTotal']);

export function ReportResultTable({ result, fieldTypes, hasSources, paramsApplied }: Props) {
  const [sortCol, setSortCol] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filter, setFilter] = useState('');
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());

  const cellValue = (row: ReportResultRow, col: string): unknown => {
    if (row.type === 'groupFooter' || row.type === 'grandTotal') {
      return row.aggregates?.[col] ?? null;
    }
    return row.data?.[col] ?? null;
  };
  const displayValue = (row: ReportResultRow, col: string): string => {
    const v = cellValue(row, col);
    const meta = fieldTypes[col];
    return formatValue(v, meta?.format, meta?.type);
  };

  const toggleSort = (col: string) => {
    if (sortCol !== col) { setSortCol(col); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortCol(''); // третий клик — без сортировки
  };

  const rows = useMemo(() => {
    const cell = (row: ReportResultRow, col: string): unknown => {
      if (row.type === 'groupFooter' || row.type === 'grandTotal') {
        return row.aggregates?.[col] ?? null;
      }
      return row.data?.[col] ?? null;
    };
    const display = (row: ReportResultRow, col: string): string => {
      const meta = fieldTypes[col];
      return formatValue(cell(row, col), meta?.format, meta?.type);
    };
    let out = result.rows;
    // Сортировка: данные сортируются ПО СЕГМЕНТАМ (внутри своих групп)
    if (sortCol) {
      const cmp = (a: ReportResultRow, b: ReportResultRow): number => {
        const va = cell(a, sortCol);
        const vb = cell(b, sortCol);
        const na = typeof va === 'number' ? va : parseFloat(String(va ?? ''));
        const nb = typeof vb === 'number' ? vb : parseFloat(String(vb ?? ''));
        let r: number;
        if (!isNaN(na) && !isNaN(nb)) r = na - nb;
        else r = String(va ?? '').localeCompare(String(vb ?? ''), 'ru');
        return sortDir === 'desc' ? -r : r;
      };
      out = [];
      let segment: ReportResultRow[] = [];
      const flush = () => {
        if (segment.length > 1) segment.sort(cmp);
        out.push(...segment);
        segment = [];
      };
      for (const row of result.rows) {
        if (row.type === 'data') { segment.push(row); } else { flush(); out.push(row); }
      }
      flush();
    }
    // Фильтр: скрываются ТОЛЬКО строки данных, не совпавшие по значению
    if (filter.trim()) {
      const needle = filter.trim().toLowerCase();
      out = out.filter((row) => row.type !== 'data' || result.columns.some((c) => display(row, c).toLowerCase().includes(needle)));
    }
    // Сворачивание групп: скрываем данные до строки уровня ≤ заголовка (итоги видны)
    if (collapsed.size > 0) {
      const hidden = new Set<number>();
      out.forEach((row, i) => {
        if (row.type === 'groupHeader' && collapsed.has(i)) {
          for (let j = i + 1; j < out.length; j++) {
            const r = out[j];
            if (r.type === 'data') { hidden.add(j); continue; }
            if (GROUP_TYPES.has(r.type) && r.level <= row.level) break;
            if (GROUP_TYPES.has(r.type)) { hidden.add(j); continue; } // вложенные заголовки/итоги
          }
        }
      });
      out = out.filter((_, i) => !hidden.has(i));
    }
    return out;
  }, [result, sortCol, sortDir, filter, collapsed, fieldTypes]);

  const dataCount = useMemo(() => rows.filter((r) => r.type === 'data').length, [rows]);

  // ТРИ сообщения пустого результата (Ф1)
  if (result.rows.length === 0 || dataCount === 0) {
    if (!hasSources) {
      return <div className="text-sm text-muted-foreground">Отчёт не настроен: не заданы источники данных.</div>;
    }
    if (filter.trim()) {
      return (
        <div className="space-y-2">
          <FilterBar value={filter} onChange={setFilter} />
          <div className="text-sm text-muted-foreground">Ничего не найдено по фильтру — измените условие.</div>
        </div>
      );
    }
    return (
      <div className="text-sm text-muted-foreground">
        {paramsApplied
          ? 'Нет данных по заданным параметрам — измените период или отбор.'
          : 'Нет данных за выбранный период (параметры по умолчанию).'}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FilterBar value={filter} onChange={setFilter} />
      <div className="overflow-auto border rounded">
        <table className="w-full text-sm">
          <thead className="bg-muted sticky top-0 z-10">
            <tr>
              {result.columns.map((c) => (
                <th key={c} className="px-2 py-1 text-left whitespace-nowrap cursor-pointer select-none hover:bg-muted/70"
                  onClick={() => toggleSort(c)} title="Сортировать">
                  {c}
                  {sortCol === c && <span className="ml-1 text-[10px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => {
              const isGroup = row.type === 'groupHeader';
              const isTotal = row.type === 'groupFooter' || row.type === 'grandTotal';
              return (
                <tr key={idx} className={isTotal ? 'bg-muted/50 font-semibold' : isGroup ? 'bg-muted/30 font-medium' : ''}>
                  {result.columns.map((c, ci) => (
                    <td key={c} className="px-2 py-1 border-t whitespace-nowrap">
                      {ci === 0 && isGroup ? (
                        <button type="button" className="flex items-center gap-1 text-left"
                          onClick={() => setCollapsed((prev) => {
                            const next = new Set(prev);
                            if (next.has(idx)) next.delete(idx); else next.add(idx);
                            return next;
                          })}>
                          {collapsed.has(idx) ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                          {displayValue(row, c)}
                        </button>
                      ) : (
                        displayValue(row, c)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterBar({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-[min(320px,100%)]">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Фильтр по значению…" className="h-8 pl-7 text-xs" />
      </div>
    </div>
  );
}
